import React from 'react';
import { Columns3, Check } from 'lucide-react';
import { getColumnPreference, saveColumnPreference } from './services/api';
import "../../styles/components/column-visibility.scss"

const SAVE_DEBOUNCE_MS = 500;

export default class ColumnVisibilityDropdown extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false, hidden: [], loading: true };
    this.wrapperRef = React.createRef();
    this.saveTimer = null;
  }

  componentDidMount() {
    this.loadPreference();
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.tableKey !== this.props.tableKey) {
      this.loadPreference();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    if (this.saveTimer) clearTimeout(this.saveTimer);
  }

  handleClickOutside = (e) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target)) {
      this.setState({ open: false });
    }
  };

  loadPreference = async () => {
    this.setState({ loading: true });
    try {
      const res = await getColumnPreference(this.props.tableKey);
      const hidden = res?.data?.hidden_columns || [];
      this.setState({ hidden });
      this.props.onChange(hidden);
    } catch (e) {
      this.setState({ hidden: [] });
      this.props.onChange([]);
    } finally {
      this.setState({ loading: false });
    }
  };

  toggleColumn = (id) => {
    const { hidden } = this.state;
    const next = hidden.includes(id) ? hidden.filter((c) => c !== id) : [...hidden, id];
    this.commitHidden(next);
  };

  // "Select all" checkbox: when everything toggleable is currently visible,
  // clicking it hides everything (except alwaysVisible columns); otherwise
  // it shows everything.
  toggleAll = () => {
    const { columns } = this.props;
    const toggleable = columns.filter((c) => !c.alwaysVisible);
    const allVisible = toggleable.every((c) => !this.state.hidden.includes(c.id));

    const next = allVisible ? toggleable.map((c) => c.id) : [];
    this.commitHidden(next);
  };

  commitHidden = (next) => {
    this.setState({ hidden: next });
    this.props.onChange(next);

    // Debounced save so rapid checkbox clicks don't fire an API call each time.
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      saveColumnPreference(this.props.tableKey, next).catch(() => {
        // Non-fatal — the table still reflects the user's choice locally,
        // it just won't have persisted. Consider a toast here if you want
        // the user to know the save failed.
      });
    }, SAVE_DEBOUNCE_MS);
  };

  render() {
    const { columns } = this.props;
    const { open, hidden, loading } = this.state;
    const visibleCount = columns.filter((c) => !hidden.includes(c.id)).length;
    const toggleable = columns.filter((c) => !c.alwaysVisible);
    const allVisible = toggleable.every((c) => !hidden.includes(c.id));
    const someVisible = toggleable.some((c) => !hidden.includes(c.id));

    return (
      <div className="column-visibility" ref={this.wrapperRef}>
        <button
          type="button"
          className="button1 button1-primary"
          onClick={() => this.setState({ open: !open })}
        >
          <Columns3 size={16} />
        Columns ({visibleCount}/{columns.length})
        </button>

        {open && (
          <div className="column-visibility__menu">
            {loading ? (
              <div className="column-visibility__loading">Loading...</div>
            ) : (
              <>
                <label className="column-visibility__item column-visibility__item--select-all">
                  <input
                    type="checkbox"
                    checked={allVisible}
                    ref={(el) => el && (el.indeterminate = !allVisible && someVisible)}
                    onChange={this.toggleAll}
                  />
                  <span className="column-visibility__checkbox">
                    {allVisible && <Check size={12} />}
                  </span>
                  Select all
                </label>
                <div className="column-visibility__divider" />
                {columns.map((col) => {
                  const isVisible = !hidden.includes(col.id);
                  return (
                    <label
                      key={col.id}
                      className={`column-visibility__item ${col.alwaysVisible ? 'is-locked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isVisible}
                        disabled={col.alwaysVisible}
                        onChange={() => this.toggleColumn(col.id)}
                      />
                      <span className="column-visibility__checkbox">
                        {isVisible && <Check size={12} />}
                      </span>
                      {col.label}
                    </label>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}